import { Component } from '@theme/component';
import { requestIdleCallback, viewTransition } from '@theme/utilities';

/**
 * Collect the actively-selected query params from the filter/sort UI.
 * - Reads all `.facets__form` inputs
 * - Preserves multi-value keys
 * - Merges extras from location.search not present in the form (e.g. sort_by)
 * - Forces filter.v.availability=1 (matching your filters.liquid)
 */
function collectActiveParams() {
  const params = new URLSearchParams();
  const seen = new Set();

  const addPair = (k, v) => {
    const key = `${k}=${v}`;
    if (seen.has(key)) return;
    seen.add(key);
    params.append(k, v);
  };

  // 1) From forms
  const forms = document.querySelectorAll('form.facets__form');
  forms.forEach((form) => {
    const elements = Array.from(form.elements || []);
    elements.forEach((el) => {
      if (!el || !el.name || el.disabled) return;

      const name = el.name;
      const tag = (el.tagName || '').toUpperCase();
      const type = (el.type || '').toLowerCase();

      if (type === 'checkbox' || type === 'radio') {
        if (el.checked) {
          const val = (el.value != null && el.value !== '') ? String(el.value) : '1';
          addPair(name, val);
        }
        return;
      }

      if (tag === 'SELECT' || tag === 'INPUT' || tag === 'TEXTAREA') {
        const val = el.value != null ? String(el.value) : '';
        if (val !== '') addPair(name, val);
      }
    });
  });

  // 2) From URL (only if not already provided by the form)
  const current = new URLSearchParams(window.location.search);
  for (const [k, v] of current.entries()) {
    if (k === 'page' || k === 'sections' || k === 'section_id') continue;
    if (!params.has(k)) addPair(k, v);
  }

  // 3) Ensure availability is always active
  if (!params.has('filter.v.availability')) {
    params.set('filter.v.availability', '1');
  }

  return params;
}

/** Create a stable signature for the current filter state */
function buildSignature() {
  const p = collectActiveParams();
  const pairs = [];
  for (const [k, v] of p.entries()) pairs.push([k, v]);
  pairs.sort((a, b) => (a[0] === b[0] ? (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0) : (a[0] < b[0] ? -1 : 1)));
  return pairs.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
}

/** Pretty URL for history (no section params) */
function buildPrettyPageUrl(page) {
  const origin = window.location.origin;
  const path = window.location.pathname;
  const searchParams = collectActiveParams();
  searchParams.set('page', String(page));

  const url = new URL(path, origin);
  for (const [k, v] of searchParams.entries()) url.searchParams.append(k, v);
  url.hash = '';
  return url;
}

/** Exact fetch URL (returns HTML) */
function buildSectionFetchUrl(page, sectionId) {
  const origin = window.location.origin;
  const path = window.location.pathname;
  const searchParams = collectActiveParams();
  searchParams.set('page', String(page));
  // IMPORTANT: use section_id (NOT sections=) so Shopify returns HTML for this section
  searchParams.set('section_id', sectionId);

  const url = new URL(path, origin);
  for (const [k, v] of searchParams.entries()) url.searchParams.append(k, v);
  url.hash = '';
  return url;
}

/** Parse section HTML and extract the li[ref="cards[]"] nodes */
function extractGridItems(html) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const grid = parsed.querySelector('[ref="grid"]');
  if (!grid) return null;
  return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
}

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLSpanElement} [viewMorePrevious]
 * @property {HTMLSpanElement} [viewMoreNext]
 * @property {HTMLElement[]} [cards]
 */
export default class PaginatedList extends Component {
  /** @type {Map<number, Element[]>} page -> prepared nodes for that page */
  pages = new Map();

  /** in-flight requests by page to avoid duplicates */
  _fetching = new Set();

  /** current cache signature */
  _sig = '';

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {string | null} */
  #imageRatioSetting = null;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();
    this.#observeViewMore();

    // 🔥 Early warm-up: prefetch the immediate next page using the current signature.
    // Safe because cache auto-invalidates if signature changes (e.g., user applies filters).
    requestIdleCallback(() => this.#prefetchInitial());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) this.infinityScrollObserver.disconnect();
  }

  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;
    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious || !viewMoreNext) return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          if (entry.target === viewMorePrevious) {
            await this.#renderPreviousPage();
          } else {
            await this.#renderNextPage();
          }
        }
      },
      // 🚀 Trigger earlier to hide latency when scrolling fast
      { rootMargin: '800px' }
    );

    this.infinityScrollObserver.observe(viewMorePrevious);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  #getLastPage() {
    const { grid } = this.refs;
    if (!grid) return 0;
    return Number(grid.getAttribute('last-page'));
  }

  #shouldUsePage(page) {
    if (!page && page !== 0) return false;
    if (page > this.#getLastPage() || page < 1) return false;
    return true;
  }

  /** Ensure cache matches current filters; clear if not */
  #ensureSignature() {
    const sig = buildSignature();
    if (sig !== this._sig) {
      this.pages.clear();
      this._sig = sig;
      // Cancel flags on any in-flight; they’ll be ignored when they resolve.
      this._fetching.clear();
    }
  }

  /** Load and cache a page if not in cache (with current signature) */
  async #ensurePageLoaded(page) {
    this.#ensureSignature();
    if (this.pages.has(page) || this._fetching.has(page)) return;

    const sigAtStart = this._sig;
    this._fetching.add(page);

    try {
      const fetchUrl = buildSectionFetchUrl(page, this.sectionId);
      const res = await fetch(fetchUrl.toString(), {
        headers: { Accept: 'text/html, */*;q=0.1' },
        credentials: 'same-origin',
      });
      if (!res.ok) return;

      const html = await res.text();

      // If signature changed during fetch, drop the result
      if (sigAtStart !== this._sig) return;

      const nodes = extractGridItems(html);
      if (nodes && nodes.length) {
        this.pages.set(page, nodes);
      }
    } finally {
      this._fetching.delete(page);
    }
  }

  /** Compute next/previous page numbers based on cards in DOM */
  #computeNextPage() {
    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return;
    const last = cards[cards.length - 1];
    return Number(last.dataset.page) + 1;
  }
  #computePreviousPage() {
    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return;
    const first = cards[0];
    return Number(first.dataset.page) - 1;
  }

  /** Initial warm prefetch for page 2 (or the next page) */
  async #prefetchInitial() {
    const nextPage = this.#computeNextPage();
    if (!this.#shouldUsePage(nextPage)) return;
    await this.#ensurePageLoaded(nextPage);
  }

  /** Prefetch just the next/previous pages after we’ve appended/prepended */
  async #prefetchFollowing(direction) {
    const last = this.#getLastPage();
    if (!last) return;

    if (direction === 'next') {
      const p1 = this.#computeNextPage();                // immediate next
      const p2 = p1 != null ? p1 + 1 : undefined;        // one more ahead
      if (this.#shouldUsePage(p1)) this.#ensurePageLoaded(p1);
      if (this.#shouldUsePage(p2)) requestIdleCallback(() => this.#ensurePageLoaded(p2));
    } else {
      const p1 = this.#computePreviousPage();
      const p2 = p1 != null ? p1 - 1 : undefined;
      if (this.#shouldUsePage(p1)) this.#ensurePageLoaded(p1);
      if (this.#shouldUsePage(p2)) requestIdleCallback(() => this.#ensurePageLoaded(p2));
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#computeNextPage();
    if (!this.#shouldUsePage(nextPage)) return;

    await this.#ensurePageLoaded(nextPage);
    const nodes = this.pages.get(nextPage);
    if (!nodes || !nodes.length) return;

    // Append and remove from cache to free memory
    grid.append(...nodes);
    this.pages.delete(nextPage);

    this.#processNewElements();

    // Update address bar (pretty URL, no section params)
    history.pushState('', '', buildPrettyPageUrl(nextPage).toString());

    // Prefetch ahead to hide latency on the next hit
    requestIdleCallback(() => this.#prefetchFollowing('next'));
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const prevPage = this.#computePreviousPage();
    if (!this.#shouldUsePage(prevPage)) return;

    await this.#ensurePageLoaded(prevPage);
    const nodes = this.pages.get(prevPage);
    if (!nodes || !nodes.length) return;

    // Preserve viewport
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    grid.prepend(...nodes);
    this.pages.delete(prevPage);

    this.#processNewElements();

    history.pushState('', '', buildPrettyPageUrl(prevPage).toString());

    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({ top: scrollTop + heightDiff, behavior: 'instant' });
    }

    requestIdleCallback(() => this.#prefetchFollowing('previous'));
  }

  /** Parse helpers for image ratios (unchanged) */
  #processNewElements() {
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }
  #getUnprocessedGalleries() { return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])'); }
  #markAsProcessed(gallery) { if (gallery instanceof HTMLElement) gallery.setAttribute('data-aspect-ratio-applied', 'true'); }
  #getSafeImageAspectRatio(width, height) { const r = width / height; return Math.max(0.1, Math.min(10, r)).toFixed(3); }
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);
    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((c) => { if (c instanceof HTMLElement) c.style.aspectRatio = aspectRatio; });
    this.#markAsProcessed(gallery);
  }
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;
    const productRatioCache = new Map();
    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;
      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }
      const img = gallery.querySelector('img');
      if (!img) { this.#applyAspectRatioToGallery(gallery, '1'); return; }
      const loadAndSet = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;
        const ratio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);
        if (productId) productRatioCache.set(productId, ratio);
        this.#applyAspectRatioToGallery(gallery, ratio);
      };
      if (img.complete) loadAndSet(); else img.addEventListener('load', loadAndSet, { once: true });
    });
  }
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;
    const ratio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!ratio) return;
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;
    requestAnimationFrame(() => { newCardGalleries.forEach((g) => { if (g instanceof HTMLElement) this.#applyAspectRatioToGallery(g, ratio); }); });
  }
  static ASPECT_RATIOS = { square: '1', portrait: '0.8', landscape: '1.778' };
  #getAspectRatioValue(name) { return PaginatedList.ASPECT_RATIOS[name] || null; }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }
}